import { Icon, type IconName } from "@sagot/ui";
import { useEffect, useRef, type ReactNode } from "react";
import type { ResultCard } from "./contract";
import type { ActionLabels } from "./labels";
import { ResultCardView } from "./result-card";

export const CARD_CLASS = "mt-2 space-y-3 rounded-lg border border-line bg-surface p-3";

export interface CardNoticeProps {
  icon: IconName;
  title: string;
  text: string;
  /** The card just became unusable (not a restored transcript): announce it and move focus here. */
  live?: boolean;
}

/** A card that can no longer be used (expired / unavailable): its title and why, nothing to press. */
export function CardNotice({ icon, title, text, live }: CardNoticeProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (live) ref.current?.focus();
  }, [live]);
  return (
    <div
      ref={ref}
      role={live ? "status" : "group"}
      aria-label={live ? undefined : title}
      tabIndex={live ? -1 : undefined}
      className="mt-2 flex items-start gap-2.5 rounded-lg border border-line bg-surface-2 p-3 outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <Icon name={icon} className="mt-0.5 size-5 text-fg-subtle" />
      <div className="text-xs">
        <p className="text-sm font-bold text-fg-muted">{title}</p>
        <p className="mt-0.5 text-fg-muted">{text}</p>
      </div>
    </div>
  );
}

export interface OutcomeInput {
  result: ResultCard | null | undefined;
  expired: boolean;
  title: string;
  labels: ActionLabels;
  timeZone: string;
  locale?: string | undefined;
  /** True when the outcome appears right after the visitor acted (focus moves to it). */
  live: boolean;
}

/** The result card, or the "expired" notice, that replaces a live card; null while the card is usable. */
export function renderOutcome({ result, expired, title, labels, timeZone, locale, live }: OutcomeInput): ReactNode {
  if (result) return <ResultCardView result={result} labels={labels} timeZone={timeZone} locale={locale} focusOnMount={live} />;
  if (expired) return <CardNotice icon="clock" title={title} text={labels.expired} live={live} />;
  return null;
}
